import {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import type { Notification } from "../types/notification.types";
import NotificationPreviewToast from "../components/NotificationPreviewToast";

interface PreviewItem {
  key: string;
  notification: Notification;
}

interface ContextValue {
  addPreview: (n: Notification) => void;
}

const NotificationPreviewContext = createContext<ContextValue>({
  addPreview: () => {},
});

export const useNotificationPreview = () =>
  useContext(NotificationPreviewContext);

function playNotificationTone() {
  try {
    const ctx = new AudioContext();
    const playNote = (freq: number, delay: number) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = "sine";
      osc.frequency.value = freq;
      const start = ctx.currentTime + delay;
      gain.gain.setValueAtTime(0.12, start);
      gain.gain.exponentialRampToValueAtTime(0.001, start + 0.35);
      osc.start(start);
      osc.stop(start + 0.35);
    };
    playNote(880, 0);
    playNote(1100, 0.18);
    setTimeout(() => ctx.close(), 800);
  } catch {
    // AudioContext unavailable or blocked by browser policy
  }
}

export const NotificationPreviewProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [previews, setPreviews] = useState<PreviewItem[]>([]);
  const counter = useRef(0);

  const addPreview = useCallback((notification: Notification) => {
    const key = `${++counter.current}-${notification.id}`;
    playNotificationTone();
    // Keep at most 3 visible at once — drop the oldest if needed
    setPreviews((prev) => [...prev.slice(-2), { key, notification }]);
  }, []);

  const removePreview = useCallback((key: string) => {
    setPreviews((prev) => prev.filter((p) => p.key !== key));
  }, []);

  return (
    <NotificationPreviewContext.Provider value={{ addPreview }}>
      {children}
      {createPortal(
        <div
          style={{
            position: "fixed",
            top: 80,
            right: 16,
            zIndex: 9999,
            display: "flex",
            flexDirection: "column",
            gap: 8,
            pointerEvents: "none",
          }}
        >
          {previews.map((p) => (
            <div key={p.key} style={{ pointerEvents: "auto" }}>
              <NotificationPreviewToast
                notification={p.notification}
                onDismiss={() => removePreview(p.key)}
              />
            </div>
          ))}
        </div>,
        document.body,
      )}
    </NotificationPreviewContext.Provider>
  );
};
